import React, {
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { Popover, Textfield, Chip } from '@workboard/wobo-ui';
import { MenuList, MenuItem } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import SearchIcon from '@material-ui/icons/Search';
import styled from 'styled-components';
import groupBy from 'lodash/groupBy';

import { useDetectOutsideClick } from '../ActionItemPopup/util/hooks';
import { Option } from '../utils/useAutocomplete';

const PopoverContent = styled.div`
  margin: -12px -24px;
  display: flex;
  flex-direction: column;
  min-width: 250px;
`;
const TextFieldWrapper = styled.div`
  padding: 4px;
  border-bottom: solid 1px #d7dde5;
  position: relative;
  & > svg {
    position: absolute;
    left: 16px;
    top: 12px;
  }
  & > div {
    width: 100%;
  }
  .MuiInputBase-formControl {
    padding-left: 40px;
  }
`;
const MenuListWrapper = styled.div`
  max-height: 350px;
  overflow: auto;
`;
const OptionCheckmark = styled.div`
  position: absolute;
  right: 8px;
`;
const GroupLabel = styled.div`
  font-weight: 700;
  padding: 10px 0;
  font-size: 16px;
`;
const ColumnLabel = styled.label`
  color: rgb(51, 57, 67);
  font-family: Lato, 'Helvetica Neue', sans-serif;
  font-size: 14px;
  margin-bottom: 8px;
  display: block;
`;

interface Props<O> {
  loading?: boolean;
  onInputChange?: (e: any, text: string) => void;
  onChange: (e: React.ChangeEvent<any>, value: O) => void;
  options: O[];
  getOptionSelected: (option: O, value: O) => boolean;
  chipTemplate?: React.ComponentType<{
    value: O;
    ref: React.Ref<any>;
    onClick: () => void;
  }>;
  optionTemplate?: (option: O) => React.ReactChild;
  value: O;
  isDisabled?: boolean;
  label?: string;
  ['data-testid']?: string;
  groupBy?: (option: O) => void;
}

const DefaultChipTemplate = React.forwardRef<
  any,
  { value: Option; onClick: () => void }
>(({ value, onClick }, ref) => (
  <Chip ref={ref} label={value.label} onClick={onClick} />
));

const ListItem = ({ selected, onClick, optionTemplate, option }) => (
  <MenuItem
    onClick={onClick}
    aria-selected={selected}
    selected={selected}
    data-testid="test-autocomplete-chip-list-item"
  >
    {optionTemplate ? optionTemplate(option) : <span>{option.label}</span>}
    {selected && (
      <OptionCheckmark>
        <CheckIcon />
      </OptionCheckmark>
    )}
  </MenuItem>
);

export const AutocompleteChip = <O extends Option>({
  loading,
  onInputChange,
  onChange,
  options,
  getOptionSelected,
  chipTemplate: ChipTemplate = DefaultChipTemplate as any,
  optionTemplate,
  isDisabled,
  value,
  groupBy: groupByCallback,
  label,
  'data-testid': testId,
}: Props<O>) => {
  const [popoverVisible, setPopoverVisible] = useState(false);
  const btnRef = useRef<HTMLElement>();
  const wrapperRef = useRef<HTMLDivElement>();
  const menuRef = useRef<HTMLUListElement>();
  useDetectOutsideClick(wrapperRef, () => setPopoverVisible(false));

  const [selectedOption, setSelectedOption] = useState<O>(value);
  useEffect(() => {
    setSelectedOption(value);
  }, [value]);

  const selectOption = useCallback(
    (e: React.MouseEvent, option: O) => {
      setSelectedOption(option);
      onChange(e, option);
      setPopoverVisible(false);
    },
    [onChange]
  );

  const handleKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      menuRef.current?.focus();
    }
    if (e.key === 'Escape') {
      setPopoverVisible(false);
    }
  }, []);

  useEffect(() => {
    if (!popoverVisible) {
      btnRef.current?.focus();
    }
  }, [popoverVisible]);

  const getListItem = useCallback(
    (opt: O) => (
      <ListItem
        key={opt.id}
        option={opt}
        selected={getOptionSelected(opt, selectedOption)}
        optionTemplate={optionTemplate}
        onClick={(e) => selectOption(e, opt)}
      />
    ),
    [getOptionSelected, optionTemplate, selectOption, selectedOption]
  );
  const memoizedOptions = useMemo(
    () =>
      groupByCallback
        ? Object.entries(groupBy(options, groupByCallback)).reduce(
            (ret, [key, opts]: [string, O[]]) => {
              ret.push(<GroupLabel key={`label-${key}`}>{key}</GroupLabel>);
              ret.push(...opts.map(getListItem));
              return ret;
            },
            []
          )
        : options.map(getListItem),
    [options, groupByCallback, getListItem]
  );

  return (
    <div ref={wrapperRef} data-testid={testId}>
      {label && <ColumnLabel>{label}</ColumnLabel>}
      <ChipTemplate
        ref={btnRef}
        value={selectedOption}
        onClick={
          isDisabled
            ? undefined
            : () => {
                setPopoverVisible((visible) => !visible);
              }
        }
      />
      <Popover
        open={popoverVisible}
        targetRef={btnRef}
        placement="bottom"
        arrow
        content={
          <PopoverContent>
            <TextFieldWrapper>
              <Textfield
                onChange={(e) => onInputChange(e, e.target.value)}
                onKeyUp={handleKeyUp}
              />
              <SearchIcon color="disabled" />
            </TextFieldWrapper>

            <MenuListWrapper>
              <MenuList ref={menuRef}>{memoizedOptions}</MenuList>
            </MenuListWrapper>
          </PopoverContent>
        }
        removeClose
        removeActions
      />
    </div>
  );
};
